import { createGlobalStyle, keyframes } from "styled-components";

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');

  :root {
    /* Primary gold palette */
    --primary-color: #d4af37; /* Rich gold */
    --primary-light: #f5e7a3; /* Light gold */
    --primary-dark: #9e7c2b; /* Dark gold */
    --secondary-color: #fbf5b7; /* Pale gold */
    --gold-gradient: linear-gradient(90deg, #bf953f, #fcf6ba, #b38728, #fbf5b7, #aa771c);
    
    /* Luxury dark palette */
    --background-color: #000000; 
    --background-secondary: #0c0c0c;
    --background-tertiary: #171717;
    --surface-color: #1a1a1a;
    
    /* Text colors */
    --text-color: #f5f5f5;
    --text-secondary: #c9c9c9;
    --text-tertiary: #8a8a8a;
    
    /* Accent colors */
    --accent-color: #e6c15a; /* Golden accent */
    --error-color: #e74c3c;
    --success-color: #2ecc71;
    
    /* Borders and effects */
    --border-color: #333333;
    --border-color-light: rgba(255, 215, 0, 0.3);
    --border-radius: 8px;
    --box-shadow: 0 4px 20px rgba(0, 0, 0, 0.7);
    --gold-shadow: 0 0 10px rgba(212, 175, 55, 0.2), 0 0 20px rgba(212, 175, 55, 0.1);
    --luxury-shadow: 0 10px 25px rgba(0, 0, 0, 0.8);
    --transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    
    /* Shine effects */
    --gold-shine: ${shimmer};
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
      Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    background-color: var(--background-color);
    color: var(--text-color);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
  }

  h1, h2, h3, h4 {
    font-family: 'Playfair Display', serif;
    margin-bottom: 1rem;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 1px;
  }
  
  h5, h6 {
    font-family: 'Cormorant Garamond', serif;
    margin-bottom: 1rem;
    font-weight: 600;
    line-height: 1.2;
  }

  p {
    font-family: 'Montserrat', sans-serif;
    margin-bottom: 1rem;
    font-weight: 300;
    letter-spacing: 0.5px;
  }

  button, input, textarea, select {
    font-family: 'Montserrat', sans-serif;
    font-size: 1rem;
  }

  input, textarea {
    background-color: var(--background-tertiary);
    color: var(--text-color);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius);
    padding: 0.75rem 1rem;
    transition: var(--transition);
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.2);
    }
    
    &::placeholder {
      color: var(--text-tertiary);
    }
  }
  
  button {
    background: var(--gold-gradient);
    background-size: 200% auto;
    color: #000;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: var(--border-radius);
    font-weight: 600;
    letter-spacing: 1px;
    transition: var(--transition);
    position: relative;
    overflow: hidden;
    box-shadow: var(--gold-shadow);
    
    &:hover {
      background-position: right center;
      box-shadow: 0 0 15px rgba(212, 175, 55, 0.4);
      transform: translateY(-2px);
    }
    
    &:after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.3) 50%,
        rgba(255, 255, 255, 0) 100%
      );
      transform: rotate(30deg);
      animation: ${shimmer} 6s infinite linear;
      pointer-events: none;
      z-index: 1;
      opacity: 0.4;
    }
  }

  a {
    color: var(--primary-color);
    text-decoration: none;
    transition: var(--transition);
    position: relative;
    
    &:hover {
      color: var(--primary-light);
    }
    
    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 0;
      height: 1px;
      background-color: var(--primary-light);
      transition: var(--transition);
    }
    
    &:hover:after {
      width: 100%;
    }
  }
  
  ::-webkit-scrollbar {
    width: 8px;
    background: var(--background-tertiary);
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(to bottom, var(--primary-dark), var(--primary-color));
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: var(--primary-color);
  }

  @media (max-width: 768px) {
    html {
      font-size: 14px;
    }
  }
`;

export default GlobalStyles;
